/**
 * NP-45 — centralised theme helpers (light/dark).
 *
 * The visual tokens live in `src/index.css` (`:root` + `.dark` CSS variables)
 * and `tailwind.config.ts` (`darkMode: ["class"]`). This module owns only the
 * *resolution + persistence* of the active theme:
 *   - device/quick persistence via localStorage (`path-theme`)
 *   - system-preference default on first use (no stored value)
 *   - applying the `dark` class to <html>
 *
 * Cross-device persistence "on the account record" (AC3) is layered on top via
 * the settings service (GET/PATCH /api/v1/borrower/me/settings) — see
 * `useTheme`. A matching pre-paint snippet in index.html applies the stored /
 * system theme before React mounts to avoid a flash of the wrong theme.
 */
export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "path-theme";

/** Coerce an unknown value (e.g. an API payload) to a valid Theme, or null. */
export function normalizeTheme(value: unknown): Theme | null {
  return value === "dark" || value === "light" ? value : null;
}

/** The user's last locally-persisted choice, if any. */
export function getStoredTheme(): Theme | null {
  try {
    return normalizeTheme(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * Stored choice → light default. The app defaults to light (dark off) until the
 * user explicitly opts in; the choice then persists (localStorage — preserved
 * across logout — and the account record).
 */
export function resolveInitialTheme(): Theme {
  return getStoredTheme() ?? "light";
}

/** Apply the theme to the document and (by default) persist it locally. */
export function applyTheme(theme: Theme, persist = true): void {
  if (typeof document !== "undefined") {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }
  if (persist) {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* storage unavailable — the in-memory + class state still applies */
    }
  }
}
